import { CloseOutlined } from "@ant-design/icons"
import { Checkbox, Empty } from "antd"
import { SearchIcon } from "lucide-react"
import { useMemo, useState } from "react"

import BaseInput from "~/components/base-input"
import { OSLoader } from "~/components/base-loader"
import { useGetPreferencedFiles } from "~/hooks/rfp-os"

import {
  FileSource,
  getMimeFromName,
  ISelectedFile,
  MEDIA_EXTS,
  normalizeFileId
} from "./types"

interface Props {
  rfpSessionId: string | undefined
  accept: string
  currentlyAttached?: string[]
  selected: ISelectedFile[]
  setSelected: (next: (prev: ISelectedFile[]) => ISelectedFile[]) => void
  maxFiles: number
}

export function SharedTab(props: Props) {
  const {
    rfpSessionId,
    accept,
    currentlyAttached,
    selected,
    setSelected,
    maxFiles
  } = props
  const [search, setSearch] = useState("")

  const { data, isLoading } = useGetPreferencedFiles(
    rfpSessionId ?? "",
    { search, tagIds: [] },
    !!rfpSessionId
  )

  const acceptList = useMemo(
    () => accept.split(",").map((s) => s.trim().toLowerCase()),
    [accept]
  )

  if (!rfpSessionId) {
    return (
      <div className="p-4 text-gray-500 text-xs">
        Shared files are only available in the RFP-OS context.
      </div>
    )
  }

  const rawFiles = (data?.data?.files ?? []) as Array<{
    id: string
    file_id?: string
    name: string
    s3_path?: string
    mime_type?: string
    file_extension?: string
    transcription_status?: "pending" | "ready" | "failed"
  }>

  const files = rawFiles.filter((f) => {
    const ext = (
      f.file_extension ??
      f.name?.split(".").pop() ??
      ""
    ).toLowerCase()
    return acceptList.includes("." + ext)
  })

  const toggle = (f: (typeof rawFiles)[number]) => {
    const ext = (
      f.file_extension ??
      f.name?.split(".").pop() ??
      ""
    ).toLowerCase()
    // See normalizeFileId comment — collapses composite/bare id shapes so
    // the picker's dedup works regardless of which source the entry came
    // from.
    const normalizedFileId = normalizeFileId(f.file_id ?? f.id)
    const sf: ISelectedFile = {
      source: "shared" as FileSource,
      file_id: normalizedFileId,
      s3_path: f.s3_path ?? "",
      file_name: f.name,
      mime_type: f.mime_type || getMimeFromName(f.name),
      is_media: MEDIA_EXTS.has(ext),
      transcription_status: f.transcription_status
    }
    setSelected((prev) => {
      if (prev.find((p) => normalizeFileId(p.file_id) === normalizedFileId)) {
        return prev.filter(
          (p) => normalizeFileId(p.file_id) !== normalizedFileId
        )
      }
      if (prev.length >= maxFiles) return prev
      return [...prev, sf]
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <BaseInput
        placeholder="Search shared files…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="!h-10 [&_input]:!h-10 [&_input]:!text-left [&_input]:!pt-3 [&_input]:!pb-3 [&_input]:!box-border [&_input]:!leading-normal [&.ant-input-affix-wrapper]:!flex [&.ant-input-affix-wrapper]:!items-center"
        allowClear={{
          clearIcon: (
            <CloseOutlined className="w-4 h-4 dark:text-dark-6 text-dark-2" />
          )
        }}
        onClear={() => setSearch("")}
        suffix={
          <SearchIcon
            className="w-4 h-4 dark:text-dark-6 text-dark-2"
            strokeWidth={1}
          />
        }
      />
      {isLoading ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <OSLoader />
        </div>
      ) : files.length === 0 ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <Empty
            description={<p className="text-xs">No shared files found.</p>}
            className="mt-0"
          />
        </div>
      ) : (
        <ul className="max-h-96 overflow-auto border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          {files.map((f) => {
            const fileId = f.file_id ?? f.id
            const fNorm = normalizeFileId(fileId)
            const checked = !!selected.find(
              (p) => normalizeFileId(p.file_id) === fNorm
            )
            const disabledByCap = !checked && selected.length >= maxFiles
            const disabledByAttached = currentlyAttached?.includes(fileId)
            return (
              <li
                key={fileId}
                className="flex items-center justify-between gap-4 py-2 px-2.5 border-b-[0.5px] border-light-border dark:border-dark-border last:border-b-0 hover:bg-gray-50 dark:hover:bg-dark-2 cursor-pointer"
                onClick={() =>
                  !disabledByCap && !disabledByAttached && toggle(f)
                }
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={checked}
                    disabled={disabledByCap || !!disabledByAttached}
                    onChange={() => toggle(f)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0"
                  />
                  <p className="text-xs dark:text-dark-6 text-dark-2 truncate">
                    {f.name}
                  </p>
                </div>
                {f.transcription_status === "pending" ? (
                  <span className="text-xs text-amber-600 shrink-0">
                    Transcribing…
                  </span>
                ) : null}
                {f.transcription_status === "failed" ? (
                  <span className="text-xs text-red-600 shrink-0">
                    Transcription failed
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
